import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { API_ENDPOINTS } from "../config/api";
import "../styles/Gallery.css";
import {
  LOCAL_GALLERY_PHOTOS,
  GALLERY_CATEGORIES,
  mergeGalleryPhotos,
  resolveGalleryImageSrc,
} from "../data/galleryData";

const API = API_ENDPOINTS.GALLERY;

const Gallery = () => {
  const [tab, setTab] = useState("photos");

  const [filters, setFilters] = useState({
    photos: "all",
    videos: "all",
  });

  const [search, setSearch] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);

  // Initialize immediately with full local photo collection for instant, zero-delay rendering
  const [photos, setPhotos] = useState(LOCAL_GALLERY_PHOTOS);
  const [videos, setVideos] = useState([]);
  const [header, setHeader] = useState(null);

  /* ================= FETCH FROM BACKEND & MERGE WITHOUT DUPLICATES ================= */
  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const res = await fetch(API);
        if (res.ok) {
          const data = await res.json();
          // Merge backend photos with local photos without duplication
          const merged = mergeGalleryPhotos(data.photos || []);
          setPhotos(merged);
          if (data.videos && data.videos.length > 0) {
            setVideos(data.videos);
          }
          if (data.header) {
            setHeader(data.header);
          }
        }
      } catch (err) {
        console.error("Gallery fetch error:", err);
      }
    };

    fetchGallery();
  }, []);

  /* ================= FILTER PHOTOS ================= */
  const filteredPhotos = photos.filter((p) => {
    const filterKey = filters.photos;
    const cat = (p.category || "").toLowerCase();

    const matchesCategory =
      filterKey === "all" ||
      cat === filterKey ||
      (filterKey === "darshan" && (cat === "temple" || cat === "darshan")) ||
      (filterKey === "poojas" &&
        (cat === "ritual" || cat === "poojas" || cat === "aarti")) ||
      (filterKey === "seva" &&
        (cat === "events" || cat === "seva" || cat === "annadhanam")) ||
      (filterKey === "deities" && cat === "deities") ||
      (filterKey === "alankaram" && cat === "alankaram") ||
      (filterKey === "guruji" && cat === "guruji");

    const matchesSearch =
      !search ||
      p.title?.toLowerCase().includes(search.toLowerCase()) ||
      p.caption?.toLowerCase().includes(search.toLowerCase());

    return matchesCategory && matchesSearch;
  });

  /* ================= FILTER VIDEOS ================= */
  const filteredVideos = videos.filter((v) => {
    const matchCategory =
      filters.videos === "all" || v.category === filters.videos;

    const matchSearch =
      !search || v.title?.toLowerCase().includes(search.toLowerCase());

    return matchCategory && matchSearch;
  });

  return (
    <div className="gallery-container">
      {/* HEADER */}
      <div className="gallery-header">
        <h1>{header?.title || "Sri Gnana Sai Baba Mandir Gallery"}</h1>
        <p>
          {header?.description ||
            "A collection of sacred moments, divine darshans, poojas, and festivals captured at Sri Gnana Sai Baba Mandir, Ambattur, Chennai."}
        </p>
      </div>

      {/* SEARCH */}
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <input
          type="text"
          placeholder="Search photos or divine darshan..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            padding: "10px 18px",
            width: "320px",
            maxWidth: "90%",
            borderRadius: "24px",
            border: "1.5px solid #e2e8f0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            outline: "none",
            fontSize: "14px",
          }}
        />
      </div>

      {/* TABS */}
      <div className="gallery-tabs">
        <button
          className={tab === "photos" ? "active" : ""}
          onClick={() => setTab("photos")}
        >
          Photos ({photos.length})
        </button>

        {videos.length > 0 && (
          <button
            className={tab === "videos" ? "active" : ""}
            onClick={() => setTab("videos")}
          >
            Videos ({videos.length})
          </button>
        )}
      </div>

      {/* ================= PHOTOS ================= */}
      {tab === "photos" && (
        <>
          <div className="category-bar">
            {GALLERY_CATEGORIES.map((cat) => {
              const count =
                cat.key === "all"
                  ? photos.length
                  : photos.filter((p) => {
                      const c = (p.category || "").toLowerCase();
                      return (
                        c === cat.key ||
                        (cat.key === "darshan" &&
                          (c === "temple" || c === "darshan")) ||
                        (cat.key === "poojas" &&
                          (c === "ritual" ||
                            c === "poojas" ||
                            c === "aarti")) ||
                        (cat.key === "seva" &&
                          (c === "events" ||
                            c === "seva" ||
                            c === "annadhanam")) ||
                        (cat.key === "deities" && c === "deities") ||
                        (cat.key === "alankaram" && c === "alankaram") ||
                        (cat.key === "guruji" && c === "guruji")
                      );
                    }).length;

              return (
                <button
                  key={cat.key}
                  className={filters.photos === cat.key ? "active" : ""}
                  onClick={() =>
                    setFilters((prev) => ({ ...prev, photos: cat.key }))
                  }
                >
                  {cat.label.toUpperCase()} {count > 0 ? `(${count})` : ""}
                </button>
              );
            })}
          </div>

          <div className="gallery-grid">
            {filteredPhotos.map((img, index) => {
              const imageSrc = resolveGalleryImageSrc(img.src || img.url);

              return (
                <div
                  className="gallery-card"
                  key={img.id || index}
                  onClick={() => setSelectedImage(img)}
                  role="button"
                  tabIndex={0}
                >
                  <img
                    src={imageSrc}
                    alt={img.title}
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      const raw = img.src || img.url || "";
                      const baseName =
                        raw.split("-").pop() || raw.split("/").pop();
                      if (
                        baseName &&
                        !e.target.src.endsWith(`/images/${baseName}`)
                      ) {
                        e.target.src = `/images/${baseName}`;
                      } else {
                        e.target.src = "/images/placeholder.jpg";
                      }
                    }}
                  />
                  <div className="gallery-overlay">
                    <h3>{img.title}</h3>
                    {img.displayCategory && (
                      <span className="gallery-glass-badge">
                        {img.displayCategory}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {filteredPhotos.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
              <p>No photos found for the selected category or search query.</p>
            </div>
          )}
        </>
      )}

      {/* ================= VIDEOS ================= */}
      {tab === "videos" && (
        <>
          <div className="category-bar">
            {["all", "aarti", "bhajan"].map((cat) => (
              <button
                key={cat}
                className={filters.videos === cat ? "active" : ""}
                onClick={() =>
                  setFilters((prev) => ({ ...prev, videos: cat }))
                }
              >
                {cat.toUpperCase()}
              </button>
            ))}
          </div>

          <div className="video-grid">
            {filteredVideos.map((vid, index) => (
              <div className="video-card" key={vid.id || index}>
                <iframe
                  src={vid.src}
                  title={vid.title}
                  loading="lazy"
                  allowFullScreen
                />
                <p>{vid.title}</p>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ================= IMAGE MODAL: High-Res Lightbox ================= */}
      {selectedImage && (
        <div
          onClick={() => setSelectedImage(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.94)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: "16px",
            zIndex: 99999,
          }}
        >
          {/* Floating close button */}
          <button
            type="button"
            onClick={() => setSelectedImage(null)}
            style={{
              position: "fixed",
              top: 24,
              right: 24,
              background: "rgba(255, 255, 255, 0.25)",
              border: "none",
              color: "#ffffff",
              width: 44,
              height: 44,
              borderRadius: "50%",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 100000,
              transition: "background 0.2s",
            }}
            aria-label="Close Preview"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Image Preview */}
          <img
            src={resolveGalleryImageSrc(
              typeof selectedImage === "object"
                ? selectedImage.src || selectedImage.url
                : selectedImage
            )}
            alt={
              typeof selectedImage === "object"
                ? selectedImage.title
                : "preview"
            }
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "90vw",
              maxHeight: "82vh",
              objectFit: "contain",
              display: "block",
              borderRadius: "12px",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.8)",
            }}
            onError={(e) => {
              const src =
                typeof selectedImage === "object"
                  ? selectedImage.src || selectedImage.url
                  : selectedImage;
              const baseName =
                src?.split("-").pop() || src?.split("/").pop();
              if (baseName && !e.target.src.endsWith(`/images/${baseName}`)) {
                e.target.src = `/images/${baseName}`;
              } else {
                e.target.src = "/images/placeholder.jpg";
              }
            }}
          />

          {/* Title & Caption */}
          {typeof selectedImage === "object" && selectedImage.title && (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                marginTop: "14px",
                textAlign: "center",
                color: "#ffffff",
                maxWidth: "600px",
              }}
            >
              <h3
                style={{
                  fontSize: "1.15rem",
                  fontWeight: 700,
                  margin: "0 0 4px 0",
                  color: "#f8fafc",
                }}
              >
                {selectedImage.title}
              </h3>
              {selectedImage.displayCategory && (
                <span className="gallery-glass-badge" style={{ marginTop: 0 }}>
                  {selectedImage.displayCategory}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Gallery;